import React from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Grow
} from '@mui/material';
import {
  FitnessCenter,
  LocalFireDepartment,
  Group,
  Schedule,
  CardMembership,
  CardGiftcard
} from '@mui/icons-material';
import dayjs from 'dayjs';
import logo_white from '../assets/the_burn_primary_logo_white_RGB.png';
import movie from '../assets/shortenVid.mp4';
import barre from '../assets/TK2_7760.jpg';
import sculpt from '../assets/SCUPLT III.jpg';
import SnowEffect from '../components/SnowEffect';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, delay }) => (
  <Grow in timeout={1000 + delay}>
    <Card
      elevation={8}
      sx={{
        height: '100%',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        },
      }}
    >
      <CardContent sx={{ textAlign: 'center', p: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  </Grow>
);

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Show holiday button until Jan 1, 2026
  const showHolidayButton = dayjs().isBefore(dayjs('2026-01-01'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E3F2FD', position: 'relative' }}>
      {/* Hero Section with Video Background */}
      <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <video
          src={movie}
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.4)',
            zIndex: 1,
          }}
        />
        
        {/* Snow Effect */}
        <SnowEffect />
        
        {/* Hero Content */}
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Fade in timeout={1500}>
            <Box>
              <Box
                component="img"
                src={logo_white}
                alt="The Burn Logo"
                sx={{
                  height: 'auto',
                  width: '100%',
                  maxWidth: isMobile ? 300 : 500,
                  mb: 4,
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                }}
              />
              
              <Typography
                variant={isMobile ? 'h4' : 'h2'}
                component="h2"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                Experience the heat, feel the burn, discover your strength
              </Typography>

              {/* CTA Buttons */}
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={3}
                sx={{ justifyContent: 'center' }}
              >
                <Button
                  variant="contained"
                  size="large"
                  href="/classes/schedule"
                  startIcon={<Schedule />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  View Class Schedule
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  href="/membership"
                  startIcon={<CardMembership />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Membership Options
                </Button>
                
                {showHolidayButton && (
                  <Button
                    variant="contained"
                    size="large"
                    href="/seasonal"
                    startIcon={<CardGiftcard />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      bgcolor: theme.palette.secondary.main,
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                      '&:hover': {
                        bgcolor: '#C0102A',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    Holiday Specials
                  </Button>
                )}
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8, position: 'relative', zIndex: 2 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ mb: 6, fontWeight: 700 }}
        >
          Why Choose The Burn?
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<FitnessCenter sx={{ fontSize: 60 }} />}
              title="All Levels Welcome"
              description="No matter where you are in your journey - beginner, intermediate, or advanced - you are welcome at The Burn! Our instructors provide modifications and create a welcoming community that makes classes accessible and fun for everyone."
              delay={0}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<LocalFireDepartment sx={{ fontSize: 60 }} />}
              title="Feel The Burn!"
              description="All classes are heated between 95-105 degrees unless otherwise specified. Experience the transformative power of heat as you sweat, strengthen, and discover your inner fire."
              delay={200}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<Group sx={{ fontSize: 60 }} />}
              title="Community & Support"
              description="Join a supportive community of like-minded individuals who are committed to their fitness journey. Make friends, stay motivated, and achieve your goals together."
              delay={400}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Image Showcase Section */}
      <Box sx={{ py: 8, bgcolor: '#E3F2FD', position: 'relative', zIndex: 2 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box
                  component="img"
                  src={sculpt}
                  alt="Sculpt Class"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: 500,
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                component="h3"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Sculpt Your Power
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                Our signature sculpt classes combine strength training with cardio to help you build lean muscle, 
                improve endurance, and achieve the toned physique you've always wanted. Each class is designed 
                to challenge and inspire you to push beyond your limits.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Barre Section */}
      <Container maxWidth="lg" sx={{ py: 8, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Typography
              variant="h4"
              component="h3"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              Barre Excellence
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Experience the grace and strength of our barre classes. Combining elements of ballet, Pilates, 
              and yoga, our barre workouts target every muscle group for a complete body transformation. 
              Perfect for building long, lean muscles and improving posture.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Fade in timeout={1000}>
              <Box
                component="img"
                src={barre}
                alt="Barre Class"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: 500,
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
              />
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Final CTA Section */}
      <Box sx={{ py: 8, bgcolor: 'primary.main', color: 'white', position: 'relative', zIndex: 2 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Ready to Start Your Journey?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join The Burn community today and discover the strength you never knew you had.
          </Typography>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={3}
            sx={{ justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              href="/classes/schedule"
              startIcon={<Schedule />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Book Your First Class
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              href="/membership"
              startIcon={<CardMembership />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              View Memberships
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
